// Medicine Borrow & Return — every call is gated server-side on the
// `pharmacy:borrow` grant and branch-scoped (routes/pharmacy-borrow.js).
import { api } from '@/lib/api/client';
import type {
  BorrowFilters, BorrowIssue, BorrowIssueDetail, BorrowReturn, BorrowPayment, BorrowSummary,
  BorrowStockBatch, BorrowHospital, CreateBorrowInput, BorrowReturnInput, BorrowPaymentInput, SettlementType,
  BorrowSettings, BorrowSettingsInput,
} from './types';

type Query = Record<string, string | undefined>;
const q = (f: BorrowFilters): Query => ({ ...f });

export function fetchBorrowIssues(filters: BorrowFilters): Promise<BorrowIssue[]> {
  return api.get('/pharmacy-borrow', { query: q(filters) });
}

export function fetchBorrowIssue(id: number): Promise<BorrowIssueDetail> {
  return api.get(`/pharmacy-borrow/${id}`);
}

export function fetchBorrowSummary(): Promise<BorrowSummary> {
  return api.get('/pharmacy-borrow/summary');
}

export function fetchBorrowReturns(filters: BorrowFilters): Promise<BorrowReturn[]> {
  return api.get('/pharmacy-borrow/returns', { query: q(filters) });
}

export function fetchBorrowPayments(filters: BorrowFilters): Promise<BorrowPayment[]> {
  return api.get('/pharmacy-borrow/payments', { query: q(filters) });
}

export function fetchBorrowStock(search: string): Promise<BorrowStockBatch[]> {
  return api.get('/pharmacy-borrow/stock', { query: { q: search } });
}

export function fetchBorrowHospitals(search: string): Promise<BorrowHospital[]> {
  return api.get('/pharmacy-borrow/hospitals', { query: { q: search } });
}

export function createBorrowIssue(input: CreateBorrowInput): Promise<BorrowIssue> {
  return api.post('/pharmacy-borrow', input);
}

export function recordBorrowReturn(id: number, input: BorrowReturnInput): Promise<{ issue: BorrowIssue; return: BorrowReturn }> {
  return api.post(`/pharmacy-borrow/${id}/returns`, input);
}

export function recordBorrowPayment(id: number, input: BorrowPaymentInput): Promise<{ issue: BorrowIssue; payment: BorrowPayment }> {
  return api.post(`/pharmacy-borrow/${id}/payments`, input);
}

export function changeBorrowSettlement(id: number, input: { settlement_type: SettlementType; item_value?: number }): Promise<BorrowIssue> {
  return api.patch(`/pharmacy-borrow/${id}/settlement`, input);
}

export function cancelBorrowIssue(id: number, reason: string): Promise<BorrowIssue> {
  return api.post(`/pharmacy-borrow/${id}/cancel`, { reason });
}

// ── Policy (Admin Panel → Pharmacy → Medicine Borrow & Return) ────────────

/** Read-only copy for the Borrow screen (pharmacy:borrow). */
export function fetchBorrowSettings(): Promise<BorrowSettings> {
  return api.get('/pharmacy-borrow/settings');
}

/** The admin side — gated on admin:pharmacy-loyalty like the rest of the Pharmacy admin tab. */
export function fetchBorrowSettingsAdmin(): Promise<BorrowSettings> {
  return api.get('/admin/pharmacy-borrow/settings');
}

export function updateBorrowSettings(input: BorrowSettingsInput): Promise<BorrowSettings> {
  return api.patch('/admin/pharmacy-borrow/settings', input);
}
